import { AfterViewInit, Component, ElementRef, HostListener, inject, OnDestroy, ViewChild } from "@angular/core";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { UiKitModule } from "@app/ui-kit.module";
import { ConfirmModalComponent } from "@components/confirm-modal/confirm-modal.component";
import { FloorplanOpacityMenuComponent } from "@components/floorplan-opacity-control/floorplan-opacity-menu.component";
import { ProjectDetailHeaderComponent } from "@components/project-detail-header/project-detail-header.component";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanEventType } from "@domain/project/floorplan/floorplan-event";
import { FloorplanSelectionMode } from "@domain/project/floorplan/floorplan-state";
import { requireScopedProject } from "@domain/project/project-deserializer";
import { ProjectFloorplanService } from "@domain/project/project-floorplan.service";
import type { ScopedProject } from "@domain/project/project-guards";
import { ProjectService } from "@domain/project/project.service";
import { UserService } from "@domain/user/user.service";
import { ModalService } from "@odx/angular/components/modal";
import { FloorplanZonesService } from "@project/floorplan-zones.service";
import { FloorplanAdditionalNotesComponent } from "@project/floorplanner/floorplan-additional-notes/floorplan-additional-notes.component";
import { FloorplanMenuType } from "@project/floorplanner/floorplan-menu/fgds/floorplan-menu-type";
import { FloorplanMenuComponent } from "@project/floorplanner/floorplan-menu/fgds/floorplan-menu.component";
import { FloorplanMultiSelectBarComponent } from "@project/floorplanner/floorplan-multi-select-bar/floorplan-multi-select-bar.component";
import { FloorplanSelectorComponent } from "@project/floorplanner/floorplan-selector/floorplan-selector.component";
import { FloorplanWorkspace } from "@project/floorplanner/floorplan-workspace";
import { FloorplanService } from "@project/floorplanner/floorplan.service";
import { FloorplanWorkspaceKonvaAdapter } from "@project/floorplanner/konva/floorplan-workspace-konva.adapter";
import { ProjectStateService } from "@project/project-state.service";
import { ToastHelper } from "@utils/toast-helper.service";
import { filter, first, map, Observable } from "rxjs";

@Component({
  selector: "app-floorplanner",
  templateUrl: "./floorplanner.component.html",
  styleUrls: ["./floorplanner.component.scss"],
  imports: [
    UiKitModule,
    RouterModule,
    ProjectDetailHeaderComponent,
    FloorplanMenuComponent,
    FloorplanAdditionalNotesComponent,
    FloorplanSelectorComponent,
    FloorplanOpacityMenuComponent,
    FloorplanMultiSelectBarComponent,
  ],
  providers: [
    FloorplanService,
    FloorplanZonesService,
    {
      provide: FloorplanWorkspace,
      useClass: FloorplanWorkspaceKonvaAdapter,
    },
  ],
})
export class FloorplannerComponent implements AfterViewInit, OnDestroy {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private toastHelper = inject(ToastHelper);
  private floorplanService = inject(FloorplanService);
  private userService = inject(UserService);
  private projectService = inject(ProjectService);
  private projectStateService = inject(ProjectStateService);
  private modalService = inject(ModalService);
  private projectFloorplanService = inject(ProjectFloorplanService);

  activeFloorplan: Floorplan;

  private inFloorplan: boolean = false;
  private copyEvent = this.copyShortcut.bind(this);
  private pasteEvent = this.pasteShortcut.bind(this);

  protected readonly FloorplanMenuType = FloorplanMenuType;
  protected readonly tooltipTextZoomIn: string = $localize`:@@floorplan.button.zoomIn:Reinzoomen`;
  protected readonly tooltipTextZoomOut: string = $localize`:@@floorplan.button.zoomOut:Rauszoomen`;
  protected readonly tooltipTextResetZoom: string = $localize`:@@floorplan.button.resetZoom:Zoom zurücksetzen`;

  protected isReadOnlyUser$: Observable<boolean>;
  protected hasWritePermission$: Observable<boolean | undefined>;

  get activeProject(): ScopedProject {
    return requireScopedProject(this.activeFloorplan.project);
  }

  @ViewChild("floorplanContainer")
  private floorplanContainer!: ElementRef;

  @ViewChild(FloorplanMenuComponent)
  private floorplanMenu!: FloorplanMenuComponent;

  constructor() {
    this.activeFloorplan = this.activatedRoute.snapshot.data["floorplan"];
    this.activeFloorplan.floorplanState.selectedGroupId = undefined;

    this.isReadOnlyUser$ = this.userService.user$.pipe(map((user) => user.readOnlyAccess));
    this.hasWritePermission$ = this.projectService.hasUserWritePermission$.pipe(
      filter((val): val is boolean => val !== undefined),
    );

    this.userService.user$.pipe(first()).subscribe((user) => {
      if (user.readOnlyAccess) {
        this.activeFloorplan.floorplanState.setLockForAll(true);
      }
    });
    this.hasWritePermission$.subscribe((hasPermission: boolean | undefined) => {
      if (!hasPermission) {
        this.activeFloorplan.floorplanState.setLockForAll(true);
      }
    });
  }

  ngAfterViewInit(): void {
    addEventListener("copy", this.copyEvent);
    addEventListener("paste", this.pasteEvent);
    this.floorplanService.init(this.floorplanContainer.nativeElement).subscribe();
  }

  ngOnDestroy(): void {
    removeEventListener("copy", this.copyEvent);
    removeEventListener("paste", this.pasteEvent);
    this.activeFloorplan.floorplanState.selectionMode = FloorplanSelectionMode.SINGLE;
    this.floorplanService.deselectItem();
  }

  @HostListener("window:keydown.delete", ["$event"])
  deleteShortcut(event: KeyboardEvent): void {
    if (!this.inFloorplan) return;
    event.preventDefault();

    const isMultiMode = this.activeFloorplan.floorplanState.selectionMode === FloorplanSelectionMode.MULTI;

    if (isMultiMode) {
      this.modalService
        .open(ConfirmModalComponent, {
          data: {
            headerText: $localize`:@@floorplan.multiSelectBar.deleteConfirm.header:Elemente löschen?`,
            text: $localize`:@@floorplan.multiSelectBar.deleteConfirm.text:Sollen alle ausgewählten Elemente wirklich gelöscht werden?`,
            confirmButtonText: $localize`:@@global.delete:Löschen`,
          },
        })
        .onClose$.subscribe(() => {
          this.floorplanService.deleteSelectedItems();
        });
    } else {
      this.floorplanService.deleteSelectedItem();
    }
  }

  @HostListener("window:keydown.space", ["$event"])
  toggleMultiSelectMode(event: KeyboardEvent): void {
    if (!this.inFloorplan) return;
    event.preventDefault();

    const currentMode = this.activeFloorplan.floorplanState.selectionMode;
    const newMode = currentMode === FloorplanSelectionMode.MULTI ? FloorplanSelectionMode.SINGLE : FloorplanSelectionMode.MULTI;
    this.activeFloorplan.floorplanState.selectionMode = newMode;

    this.projectFloorplanService.publishFloorplanEvent(
      this.activeFloorplan,
      FloorplanEventType.FLOORPLAN_SELECTION_MODE_UPDATED,
      newMode,
    );

    if (this.floorplanMenu) {
      this.floorplanMenu.open(FloorplanMenuType.MAIN_SETTINGS);
    }
  }

  copyShortcut(event: any): void {
    if (!this.inFloorplan) return;
    event.preventDefault();
    const copiedCount = this.floorplanService.copySelectedItem();
    if (copiedCount === 1) {
      this.toastHelper.message($localize`:@@toast.floorplanElementCopySuccessful:Element wurde erfolgreich kopiert!`);
    } else if (copiedCount > 1) {
      this.toastHelper.message($localize`:@@toast.floorplanElementsCopySuccessful:Elemente wurden erfolgreich kopiert!`);
    }
  }

  pasteShortcut(event: any): void {
    if (!this.inFloorplan) return;
    event.preventDefault();
    this.floorplanService.pasteCopiedItem();
  }

  onEnterFloorplan() {
    this.inFloorplan = true;
  }

  onLeaveFloorplan() {
    this.inFloorplan = false;
  }

  onZoomOut() {
    this.floorplanService.zoomOut();
  }

  onZoomIn() {
    this.floorplanService.zoomIn();
  }

  onResetZoom() {
    this.floorplanService.resetZoom();
  }

  navigateBack() {
    this.router.navigate([`/projects/${this.activeProject.id}`]);
  }
}
